import apiClient from './apiClient';
import { Product, ProductListResponse, ProductFilter } from '../types';

export interface AdminPurchaseReceipt {
  receiptId: number;
  unitCost: number;
}

export interface AdminProductVariant {
  id: number;
  price: number;
  versionName: string;
  color: string;
  inventory?: {
    quantity: number;
  };
  purchaseReceipts?: AdminPurchaseReceipt[];
}

export interface AdminProduct {
  id: number;
  name: string;
  productVariants?: AdminProductVariant[];
  brand?: {
    name: string;
  };
  category?: {
    name: string;
  };
}

export interface AdminProductListResponse {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  data: AdminProduct[];
}

interface AdminProductListApiResponse {
  code: number | string;
  message: string;
  result: AdminProductListResponse;
}

export interface StoreProductVariant {
  id: number;
  productId: number;
  price: number;
  versionName: string;
  color: string;
  inventory: {
    quantity: number;
  } | null;
}

export interface StoreProduct {
  id: number;
  name: string;
  productVariants?: StoreProductVariant[];
  category?: {
    name: string;
  };
}

export interface StoreProductListResponse {
  code: number | string;
  message: string;
  result: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalElements: number;
    data: StoreProduct[];
  };
}

export const getAdminProducts = async (page = 1, limit = 25): Promise<AdminProductListResponse> => {
  const response = await apiClient.get<AdminProductListApiResponse>('/api/products/admin', {
    params: { page, limit },
  });

  return response.data.result;
};

export const getStoreProducts = async (page = 1, limit = 25): Promise<StoreProductListResponse['result']> => {
  const response = await apiClient.get<StoreProductListResponse>('/api/products', {
    params: { page, limit },
  });

  return response.data.result;
};

// Lấy danh sách sản phẩm
export const getProducts = async (filters: ProductFilter): Promise<ProductListResponse> => {
  const params = new URLSearchParams();
  
  if (filters.category) params.append('category', filters.category);
  if (filters.brand) params.append('brand', filters.brand);
  if (filters.minPrice) params.append('minPrice', filters.minPrice.toString());
  if (filters.maxPrice) params.append('maxPrice', filters.maxPrice.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());

  const response = await apiClient.get(`/products?${params.toString()}`);
  return response as any; // Type assertion - sẽ fix khi có backend API
};

// Lấy chi tiết sản phẩm theo ID
export const getProductById = async (id: string): Promise<Product> => {
  const response = await apiClient.get(`/products/${id}`);
  return response as any; // Type assertion - sẽ fix khi có backend API
};

// Lấy sản phẩm theo danh mục
export const getProductsByCategory = async (category: string): Promise<Product[]> => {
  const response = await apiClient.get(`/products/category/${category}`);
  return response as any; // Type assertion - sẽ fix khi có backend API
};

// Lấy sản phẩm nổi bật
export const getFeaturedProducts = async (): Promise<Product[]> => {
  const response = await apiClient.get('/products/featured');
  return response as any; // Type assertion - sẽ fix khi có backend API
};

// Lấy sản phẩm mới nhất
export const getNewProducts = async (limit: number = 8): Promise<Product[]> => {
  const response = await apiClient.get(`/products/new?limit=${limit}`);
  return response as any; // Type assertion - sẽ fix khi có backend API
};

// Lấy sản phẩm bán chạy
export const getBestSellerProducts = async (limit: number = 8): Promise<Product[]> => {
  const response = await apiClient.get(`/products/bestseller?limit=${limit}`);
  return response as any; // Type assertion - sẽ fix khi có backend API
};

// Tìm kiếm sản phẩm
export const searchProducts = async (query: string): Promise<Product[]> => {
  const response = await apiClient.get(`/products/search?q=${query}`);
  return response as any; // Type assertion - sẽ fix khi có backend API
};
